import { libraryRightNameSchema } from "./library-rights.js";
import { z } from "zod";
//#region src/library/library-topics.ts
/**
* The grammar of library event topics, and the first tokens that app and library topics may not
* take (2026-10-12-shared-libraries, R-024, R-026, R-030).
*
* A consuming app and its library instance exchange events by publish into the other side's subject
* tree (R-026):
*
* - inbound, the consuming app to the instance: `zion.<instance>.in.<topic>.<version>`;
* - outbound, the instance to the consuming app: `zion.<consumer>.lib.<library-slug>.<topic>.<version>`.
*
* So the token `in` belongs to the library trees of an instance's own stream, and the token `lib`
* belongs to the library trees of a consuming app's own stream. Two rules keep each app's own topics
* out of those trees (R-030, final-3):
*
* 1. No app topic has `lib` as its first token.
* 2. No library topic, and no own topic of a library, has `in` as its first token.
*
* A publish with `PublishOptions.instanceId` puts the per-instance key in the same subject position,
* `zion.<app>.<instanceKey>.<topic>.<version>`. So a third rule refuses a per-instance key whose first
* token is `lib` or `in`, for every app (WS-0-R8). `perInstanceKeyRefusal` in `event-subjects.ts` holds it.
*
* A library topic holds no dot, so no library topic is a prefix of another (R-026 item 2).
*
* Pure and browser-safe. The subject builders are in `event-subjects.ts`.
*/
/**
* The grammar of one library event topic, inbound or outbound: a lower-case letter, then up to 62
* characters of `[a-z0-9_-]`. No dot, no wildcard and no upper case. `job-done` matches; `job.done`
* and `Job` do not. A topic that also equals {@link LIBRARY_TOPIC_RESERVED_TOKEN} is refused by
* {@link isLibraryEventTopic}.
*/
const LIBRARY_EVENT_TOPIC_PATTERN = /^[a-z][a-z0-9_-]{0,62}$/;
/** The longest library event topic. */
const LIBRARY_EVENT_TOPIC_MAX_LENGTH = 63;
/** The most topics a library declares in one direction, `events.inbound[]` or `events.outbound[]`. */
const LIBRARY_EVENT_TOPICS_MAX = 32;
/** The first token no app topic may take: the consuming app's outbound tree `zion.<consumer>.lib.…` (R-030). */
const APP_TOPIC_RESERVED_TOKEN = "lib";
/** The first token no library topic may take: the instance's inbound tree `zion.<instance>.in.…` (R-030). */
const LIBRARY_TOPIC_RESERVED_TOKEN = "in";
/**
* True when `topic` is a string that matches {@link LIBRARY_EVENT_TOPIC_PATTERN} and is not
* {@link LIBRARY_TOPIC_RESERVED_TOKEN}. Never throws. Pure.
*/
function isLibraryEventTopic(topic) {
	return typeof topic === "string" && topic.length <= 63 && LIBRARY_EVENT_TOPIC_PATTERN.test(topic) && topic !== "in";
}
/** The first dot-delimited token of `topic`: `a` for `a.b.c`, the whole text for a topic with no dot. Pure. */
function firstTopicToken(topic) {
	const dot = topic.indexOf(".");
	return dot === -1 ? topic : topic.slice(0, dot);
}
/** The zod form of one library event topic: the length cap, then {@link isLibraryEventTopic}. */
const libraryEventTopicSchema = z.string().max(63).refine(isLibraryEventTopic, { message: "not a library event topic" });
/** Adds one issue at `<index>.topic` for each event whose topic an earlier event of the list holds. */
function refuseRepeatedTopics(events, ctx) {
	const seen = /* @__PURE__ */ new Set();
	events.forEach((event, index) => {
		if (seen.has(event.topic)) ctx.addIssue({
			code: "custom",
			path: [index, "topic"],
			message: `topic ${event.topic} is declared twice`
		});
		seen.add(event.topic);
	});
}
/**
* The zod form of the `app.json` `events` member of a library (R-024, R-026, contracts-WS-0-R10).
* Strict at each level. It refuses:
*
* 1. a topic off {@link LIBRARY_EVENT_TOPIC_PATTERN}, over 63 characters, or equal to
*    {@link LIBRARY_TOPIC_RESERVED_TOKEN};
* 2. an inbound `right` off the library right grammar;
* 3. more than {@link LIBRARY_EVENT_TOPICS_MAX} (32) events in one direction;
* 4. two inbound events with the same topic, or two outbound events with the same topic (path
*    `inbound.<index>.topic` or `outbound.<index>.topic` of the second).
*
* Rule 4 keeps each inbound topic bound to exactly one library right (R-024 item 1): with two
* inbound entries for one topic, a holder of either right could publish it. One topic may appear
* once inbound and once outbound, because the two directions are two subject trees (`in.` and
* `lib.`). The JSON Schema twin refuses only two equal members (`uniqueItems`), so the publish door
* (WS-2) parses the member with this schema. It does not check that an inbound right is declared in
* `libraryRights[]`; that is a publish rule.
*/
const libraryEventsSchema = z.strictObject({
	inbound: z.array(z.strictObject({
		topic: libraryEventTopicSchema,
		right: libraryRightNameSchema
	})).max(32).superRefine((events, ctx) => refuseRepeatedTopics(events, ctx)).optional(),
	outbound: z.array(z.strictObject({ topic: libraryEventTopicSchema })).max(32).superRefine((events, ctx) => refuseRepeatedTopics(events, ctx)).optional()
});
//#endregion
export { APP_TOPIC_RESERVED_TOKEN, LIBRARY_EVENT_TOPICS_MAX, LIBRARY_EVENT_TOPIC_MAX_LENGTH, LIBRARY_EVENT_TOPIC_PATTERN, LIBRARY_TOPIC_RESERVED_TOKEN, firstTopicToken, isLibraryEventTopic, libraryEventsSchema };

//# sourceMappingURL=library-topics.js.map